import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  canonical?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  canonical,
  schema,
}: SEOProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      if (!ogDescription) {
        updateMetaTag('og:description', description, true);
        updateMetaTag('twitter:description', description);
      }
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
      updateMetaTag('twitter:title', ogTitle);
    } else if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
      updateMetaTag('twitter:description', ogDescription);
    }

    // Update canonical URL
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
      linkElement.setAttribute('href', `${siteUrl}${canonical}`);
      
      updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    }

    // Add Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let scriptElement = document.getElementById(scriptId);
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.id = scriptId;
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
      const schemaWithUrl = JSON.parse(JSON.stringify(schema).replace(/https:\/\/example\.com/g, siteUrl));
      scriptElement.textContent = JSON.stringify(schemaWithUrl);
    }

    return () => {
      // Cleanup if needed
    };
  }, [title, description, keywords, ogTitle, ogDescription, canonical, schema]);
};

export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'KR스튜디오',
  url: 'https://example.com',
  logo: 'https://example.com/logo.png',
  description: '24시간 내 완성되는 교육 영상, 300개 이상 교육기관이 선택한 KR스튜디오. 기획부터 운영까지 원스탑 솔루션을 제공합니다.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'KR',
    addressLocality: '서울',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+82-2-1234-5678',
    contactType: '고객 서비스',
    availableLanguage: 'Korean',
  },
  founder: [
    {
      '@type': 'Person',
      name: '김민규',
    },
    {
      '@type': 'Person',
      name: '류인국',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.91',
    bestRating: '5',
    ratingCount: '300',
  },
});

export const getServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: '교육 콘텐츠 제작',
  provider: {
    '@type': 'Organization',
    name: 'KR스튜디오',
    url: 'https://example.com',
  },
  areaServed: {
    '@type': 'Country',
    name: '대한민국',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: '교육 콘텐츠 제작 서비스',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'VOD 콘텐츠 제작',
          description: '전자칠판, 크로마키, 태블릿, AI 콘텐츠 제작',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '라이브 스트리밍',
          description: '실시간 라이브 송출 서비스',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '현장 촬영',
          description: '세미나 촬영 및 출장 지원',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '스튜디오 컨설팅',
          description: '설계, 장비구성, 운영교육',
        },
      },
    ],
  },
});

export const getWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'KR스튜디오',
  url: 'https://example.com',
  description: '24시간 내 완성되는 교육 영상, 300개 이상 교육기관이 선택한 KR스튜디오. 기획부터 운영까지 원스탑 솔루션을 제공합니다.',
  inLanguage: 'ko-KR',
  publisher: {
    '@type': 'Organization',
    name: 'KR스튜디오',
    logo: {
      '@type': 'ImageObject',
      url: 'https://example.com/logo.png',
    },
  },
});
